// Calendar days in the learner's own time zone, for XP days and streaks. A day ends at the learner's midnight, so a
// learner in Bharat no longer sees "today" change at 5:30 in the morning (UTC midnight). Pure: browser and server.

/** For an account or guest whose time zone is not known yet. Most learners are in Bharat. */
export const DEFAULT_TIME_ZONE = 'Asia/Kolkata'

const formatters = new Map<string, Intl.DateTimeFormat>()

const formatterFor = (timeZone: string): Intl.DateTimeFormat => {
    let formatter = formatters.get(timeZone)

    if (!formatter) {
        formatter = new Intl.DateTimeFormat('en-US', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' })
        formatters.set(timeZone, formatter)
    }

    return formatter
}

/** Whether `value` is a time zone this runtime knows (an IANA name such as "Asia/Kolkata" or "Europe/London"). */
export const isTimeZone = (value: unknown): value is string => {
    if (typeof value !== 'string' || value.length < 1 || value.length > 64) return false

    try {
        formatterFor(value)

        return true
    }
    catch {
        return false
    }
}

/** The time zone to use: the given one when it is valid, else the default. */
export const timeZoneOr = (value: unknown): string => (isTimeZone(value) ? value : DEFAULT_TIME_ZONE)

/** The calendar day (YYYY-MM-DD) that `date` falls on in `timeZone`. */
export const dayIn = (timeZone: string, date: Date = new Date()): string => {
    const parts = formatterFor(timeZoneOr(timeZone)).formatToParts(date)
    const part = (type: string) => parts.find(p => p.type === type)?.value ?? '00'

    return `${part('year')}-${part('month')}-${part('day')}`
}

/** `day` moved by `amount` calendar days (negative goes back). */
export const addDays = (day: string, amount: number): string => {
    const date = new Date(`${day}T00:00:00Z`)

    date.setUTCDate(date.getUTCDate() + amount)

    return date.toISOString().slice(0, 10)
}

/** The Monday of the week `day` falls in. */
export const weekStartOf = (day: string): string => {
    const weekday = new Date(`${day}T00:00:00Z`).getUTCDay()

    return addDays(day, -((weekday + 6) % 7))
}

/** Whole days from `from` to `to` (both YYYY-MM-DD). */
export const daysBetween = (from: string, to: string): number => Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86400000)

/** The browser's own time zone (call only in the browser, after mount). */
export const browserTimeZone = (): string => {
    try {
        return timeZoneOr(Intl.DateTimeFormat().resolvedOptions().timeZone)
    }
    catch {
        return DEFAULT_TIME_ZONE
    }
}
